import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Star, Loader2, CheckCircle2, MessageCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { SOCIAL } from "../data/siteData";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { trackEvent, attributionForSubmission } from "../lib/analytics";

const CITY_OPTIONS = [
    { slug: "jabalpur", label: "Jabalpur" },
    { slug: "bhopal", label: "Bhopal" },
    { slug: "nagpur", label: "Nagpur" },
    { slug: "online", label: "1:1 Online Tuition (any city)" },
    { slug: "other", label: "Other / Not listed" },
];

const GRADE_OPTIONS = [
    "Class 1-5", "Class 6-8", "Class 9", "Class 10", "Class 11", "Class 12",
    "NEET / Medical Entrance", "JEE / Engineering Entrance", "IB / IGCSE / A-Level", "Other",
];

export default function LeaveReview() {
    const { cityParam } = useParams();
    const navigate = useNavigate();
    const initialCity = (CITY_OPTIONS.find((c) => c.slug === cityParam) || {}).slug || "";
    const [form, setForm] = useState({
        parent_name: "", parent_email: "", parent_phone: "",
        student_grade: "", city: initialCity, locality: "", subject_or_board: "",
        rating: 5, review_title: "", review_body: "", consent_publish: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        trackEvent("review_form_opened", { review_city: initialCity || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.parent_name || !form.city || !form.review_body) {
            toast.error("Please add your name, city and review.");
            return;
        }
        if (form.review_body.length < 20) {
            toast.error("Please write at least 20 characters about your experience.");
            return;
        }
        setSubmitting(true);
        try {
            const attribution = attributionForSubmission();
            const payload = { ...form, rating: Number(form.rating), attribution };
            const { data } = await api.post("/reviews/submit", payload);
            trackEvent("review_submitted", {
                review_id: data?.id,
                rating: Number(form.rating),
                review_city: form.city,
            });
            toast.success("Thank you — your review has been received.");
            setDone(true);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="bg-white">
                <SEO
                    title="Thank You — Review Received | Anand Tutorials"
                    description="Thank you for sharing your experience with Anand Tutorials. Your review will be published after a quick moderation check."
                    path="/leave-review/thanks"
                    noindex
                />
                <PageHeader title="Thank you, your review has been received." subtitle="We moderate every review before publishing to keep things genuine. You'll typically see it live within 24 hours." />
                <section className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
                        <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                        <h2 className="font-heading text-2xl font-bold text-[#0F172A] mb-2">Submitted ✓</h2>
                        <p className="text-slate-600 mb-6">
                            We genuinely read every word. Reviews help other parents make a confident decision —
                            thank you for taking the time.
                        </p>
                        <div className="flex flex-wrap justify-center gap-3">
                            <Link to="/" data-testid="review-thanks-home" className="px-5 py-2.5 rounded-lg bg-[#1E3A8A] text-white font-semibold hover:bg-[#1d3478]">Back to Home</Link>
                            <a
                                href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent("Hi Anand Tutorials — I just submitted a review.")}`}
                                target="_blank" rel="noopener noreferrer"
                                data-testid="review-thanks-wa"
                                className="px-5 py-2.5 rounded-lg bg-[#22C55E] text-white font-semibold hover:bg-[#16A34A] inline-flex items-center gap-2"
                            >
                                <MessageCircle className="w-4 h-4" /> WhatsApp us
                            </a>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="bg-white">
            <SEO
                title="Share Your Experience — Leave a Review | Anand Tutorials"
                description="Anand Tutorials parents — share your 1:1 home tuition or online tuition experience. Reviews are moderated and help other families across Jabalpur, Bhopal, Nagpur and India."
                keywords="leave a review anand tutorials, parent review home tuition, anand tutorials testimonials, anand tutorials reviews jabalpur bhopal nagpur"
                path={cityParam ? `/leave-review/${cityParam}` : "/leave-review"}
                noindex
            />
            <PageHeader
                title="Share your experience"
                subtitle="Help other parents make a confident decision. Reviews go live after a 24-hour moderation check."
            />
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
                <Breadcrumbs items={[{ name: "Leave a Review" }]} />
            </div>

            <section className="max-w-3xl mx-auto px-6 lg:px-8 pb-16">
                <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#1E3A8A] mb-6"
                    data-testid="review-back"
                >
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>

                <form onSubmit={submit} data-testid="review-form" className="space-y-5 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm">
                    {/* Rating */}
                    <div>
                        <label className="block text-sm font-semibold text-[#0F172A] mb-2">Your rating</label>
                        <div className="flex items-center gap-1" data-testid="review-stars">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => setForm({ ...form, rating: n })}
                                    data-testid={`review-star-${n}`}
                                    className="p-1"
                                    aria-label={`${n} stars`}
                                >
                                    <Star className={`w-7 h-7 transition-colors ${n <= form.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                                </button>
                            ))}
                            <span className="ml-3 text-sm text-slate-500">{form.rating} / 5</span>
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Your name" required>
                            <input
                                required value={form.parent_name} onChange={update("parent_name")}
                                data-testid="review-name"
                                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-2 focus:ring-blue-100 outline-none"
                                placeholder="Parent's name"
                            />
                        </Field>
                        <Field label="Email (optional)">
                            <input
                                type="email" value={form.parent_email} onChange={update("parent_email")}
                                data-testid="review-email"
                                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-2 focus:ring-blue-100 outline-none"
                                placeholder="you@example.com"
                            />
                        </Field>
                        <Field label="Phone (optional)">
                            <input
                                value={form.parent_phone} onChange={update("parent_phone")}
                                data-testid="review-phone"
                                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-2 focus:ring-blue-100 outline-none"
                                placeholder="+91 …"
                            />
                        </Field>
                        <Field label="City" required>
                            <select
                                required value={form.city} onChange={update("city")}
                                data-testid="review-city"
                                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#1E3A8A] focus:ring-2 focus:ring-blue-100 outline-none"
                            >
                                <option value="">Select your city / format</option>
                                {CITY_OPTIONS.map((c) => (
                                    <option key={c.slug} value={c.slug}>{c.label}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Locality / area (optional)">
                            <input
                                value={form.locality} onChange={update("locality")}
                                data-testid="review-locality"
                                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-2 focus:ring-blue-100 outline-none"
                                placeholder="e.g. Vijay Nagar, MP Nagar, Dharampeth"
                            />
                        </Field>
                        <Field label="Student grade">
                            <select
                                value={form.student_grade} onChange={update("student_grade")}
                                data-testid="review-grade"
                                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#1E3A8A] focus:ring-2 focus:ring-blue-100 outline-none"
                            >
                                <option value="">Select grade</option>
                                {GRADE_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
                            </select>
                        </Field>
                        <Field label="Subject / board (optional)">
                            <input
                                value={form.subject_or_board} onChange={update("subject_or_board")}
                                data-testid="review-subject"
                                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-2 focus:ring-blue-100 outline-none"
                                placeholder="e.g. CBSE Class 10 Maths, NEET Bio"
                            />
                        </Field>
                        <Field label="Review title (optional)">
                            <input
                                value={form.review_title} onChange={update("review_title")}
                                data-testid="review-title"
                                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-2 focus:ring-blue-100 outline-none"
                                placeholder="One line summary"
                                maxLength={120}
                            />
                        </Field>
                    </div>

                    <Field label="Your review" required hint="What went well? How was the tutor? Anything specific that helped your child?">
                        <textarea
                            required rows={6}
                            value={form.review_body} onChange={update("review_body")}
                            data-testid="review-body"
                            className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-[#1E3A8A] focus:ring-2 focus:ring-blue-100 outline-none"
                            placeholder="Share your honest experience…"
                            minLength={20}
                            maxLength={2000}
                        />
                        <div className="text-xs text-slate-400 mt-1">{form.review_body.length} / 2000</div>
                    </Field>

                    <label className="flex items-start gap-2 text-sm text-slate-600">
                        <input
                            type="checkbox" checked={form.consent_publish}
                            onChange={(e) => setForm({ ...form, consent_publish: e.target.checked })}
                            data-testid="review-consent"
                            className="mt-0.5"
                        />
                        <span>I agree that Anand Tutorials may publish my review (first name + last initial) on the website and Google Business Profile.</span>
                    </label>

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="review-submit"
                        className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 disabled:opacity-60 transition-colors"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
                        {submitting ? "Submitting…" : "Submit Review"}
                    </button>
                </form>
            </section>
        </div>
    );
}

const Field = ({ label, required, hint, children }) => (
    <label className="block">
        <span className="block text-sm font-semibold text-[#0F172A] mb-1.5">
            {label} {required && <span className="text-red-500">*</span>}
        </span>
        {children}
        {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </label>
);
